import type { NodeMock, StatePreset } from "../mock-types";

export const autoBalanceMock: NodeMock = {
  label: "Auto Balance",
  description: "Teams balanced automatically by ELO",
  state: (players, teamCount) => {
    // Internal tracking: teamIndex -> playerIds (for choosing best team)
    const teamPlayers: string[][] = Array.from({ length: teamCount }, () => []);
    const teamElos: number[] = Array.from({ length: teamCount }, () => 0);

    // Group party members by partyId, then assign each party to the smallest team
    const partyGroups = new Map<string, typeof players>();
    const soloPlayers: typeof players = [];
    for (const p of players) {
      if (p.partyId !== null) {
        const group = partyGroups.get(p.partyId) ?? [];
        group.push(p);
        partyGroups.set(p.partyId, group);
      } else {
        soloPlayers.push(p);
      }
    }

    // Place each party group on the team with fewest players
    for (const group of partyGroups.values()) {
      let bestTeam = 0;
      for (let t = 1; t < teamCount; t++) {
        if (
          teamPlayers[t].length < teamPlayers[bestTeam].length ||
          (teamPlayers[t].length === teamPlayers[bestTeam].length &&
            teamElos[t] < teamElos[bestTeam])
        ) {
          bestTeam = t;
        }
      }
      for (const p of group) {
        teamPlayers[bestTeam].push(p.userId);
        teamElos[bestTeam] += p.displayElo;
      }
    }

    // Greedy-assign solo players across teams, filling smallest team first
    const sorted = [...soloPlayers].sort((a, b) => b.displayElo - a.displayElo);
    for (const p of sorted) {
      let bestTeam = 0;
      for (let t = 1; t < teamCount; t++) {
        if (
          teamPlayers[t].length < teamPlayers[bestTeam].length ||
          (teamPlayers[t].length === teamPlayers[bestTeam].length &&
            teamElos[t] < teamElos[bestTeam])
        ) {
          bestTeam = t;
        }
      }
      teamPlayers[bestTeam].push(p.userId);
      teamElos[bestTeam] += p.displayElo;
    }

    // Output format matches backend: userId -> teamNumber (0-indexed)
    const teams: Record<string, number> = {};
    for (let t = 0; t < teamCount; t++) {
      for (const uid of teamPlayers[t]) {
        teams[uid] = t;
      }
    }

    return { teams, teamElos };
  },
  config: {
    algorithm: "greedy",
    labels: {
      title: "Team Assignment",
      subtitle: "Teams balanced by rating",
    },
  },
};

export const autoBalancePresets: StatePreset[] = [
  {
    label: "Even Split",
    build: ({ players, teamCount }) => {
      const teams: Record<string, number> = {};
      const teamElos: number[] = Array.from({ length: teamCount }, () => 0);
      // Perfectly even round-robin
      players.forEach((p, i) => {
        const t = i % teamCount;
        teams[p.userId] = t;
        teamElos[t] += p.displayElo;
      });
      return { state: { teams, teamElos } };
    },
  },
  {
    label: "Stacked Team 1",
    build: ({ players, teamCount }) => {
      const teams: Record<string, number> = {};
      const teamElos: number[] = Array.from({ length: teamCount }, () => 0);
      // Put highest-ELO players on team 0
      const sorted = [...players].sort((a, b) => b.displayElo - a.displayElo);
      const perTeam = Math.ceil(players.length / teamCount);
      sorted.forEach((p, i) => {
        const t = Math.min(Math.floor(i / perTeam), teamCount - 1);
        teams[p.userId] = t;
        teamElos[t] += p.displayElo;
      });
      return { state: { teams, teamElos } };
    },
  },
];
